import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Clock, Users, ChevronRight, GraduationCap, MapPin, CheckCircle2, CreditCard, Wallet, TicketPercent, Hourglass } from 'lucide-react';
import { motion } from 'framer-motion';

const WEEKDAYS = [
  { en: 'Monday',    de: 'Montag' },
  { en: 'Tuesday',   de: 'Dienstag' },
  { en: 'Wednesday', de: 'Mittwoch' },
  { en: 'Thursday',  de: 'Donnerstag' },
  { en: 'Friday',    de: 'Freitag' },
  { en: 'Saturday',  de: 'Samstag' },
  { en: 'Sunday',    de: 'Sonntag' },
];

/**
 * Trainings — customer-facing "book a training lesson" browse page.
 * Backed by:
 *   GET  /api/trainers/availability?duration=&participants=&days=
 *   POST /api/trainer/customer-book-lesson
 *
 * Rules enforced by the backend:
 *   * >24h advance notice
 *   * No orphan gaps on the chosen court
 *   * Only 60/90/120 min lessons at full-hour starts
 *   * Price sourced from official /pricing/training matrix
 */
const Trainings = () => {
  const { API, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);

  const [duration] = useState(60); // Customer training is 1h only — per official pricing
  const [participants, setParticipants] = useState(1);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrainerId, setSelectedTrainerId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // yyyy-mm-dd
  const [confirming, setConfirming] = useState(null); // slot object being confirmed
  const [booking, setBooking] = useState(false);
  const [groupPrograms, setGroupPrograms] = useState([]);
  const [enrolling, setEnrolling] = useState(null); // program id being toggled
  const [currentUserId, setCurrentUserId] = useState(null);
  const [myCredits, setMyCredits] = useState(0);
  const [myVouchers, setMyVouchers] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [enrolDialog, setEnrolDialog] = useState(null);
  const [payMethod, setPayMethod] = useState('card');
  const [chosenVoucherId, setChosenVoucherId] = useState('');

  // Group programs by (title + weekday + start_time) so a whole weekly
  // series collapses to a single browseable card on /trainings.
  const programGroups = useMemo(() => {
    const map = new Map();
    for (const p of groupPrograms) {
      const key = `${p.title}||${p.weekday}||${p.start_time}||${p.duration_minutes}||${(p.trainer_ids || []).join(',') || p.trainer_id || ''}`;
      if (!map.has(key)) {
        map.set(key, { key, title: p.title, weekday: p.weekday, start_time: p.start_time, duration_minutes: p.duration_minutes, trainer_name: p.trainer_name, price: p.price, description: p.description, registration_only: !!p.registration_only, sessions: [] });
      }
      map.get(key).sessions.push(p);
    }
    // Sort sessions inside each group by session_date (asc). Groups themselves
    // are sorted by the earliest upcoming session.
    for (const g of map.values()) {
      g.sessions.sort((a, b) => (a.session_date || '').localeCompare(b.session_date || ''));
      const active = g.sessions.filter(s => (s.spots_left ?? 0) > 0).length;
      g.active_sessions = active;
      g.total_sessions = g.sessions.length;
      g.next_date = g.sessions[0]?.session_date || null;
    }
    return Array.from(map.values()).sort((a, b) => (a.next_date || '').localeCompare(b.next_date || ''));
  }, [groupPrograms]);

  const loadGroupPrograms = async () => {
    try {
      const [{ data: progs }, meRes, vouchRes] = await Promise.all([
        API.get('/training-programs'),
        API.get('/auth/me').catch(() => ({ data: null })),
        API.get('/vouchers/my').catch(() => ({ data: [] })),
      ]);
      setGroupPrograms(Array.isArray(progs) ? progs : []);
      setCurrentUserId(meRes?.data?.id || null);
      setMyCredits(Number(meRes?.data?.credits_balance || 0));
      const activeV = (vouchRes?.data || []).filter(v => v.is_active && Number(v.remaining_balance || v.amount || 0) > 0);
      setMyVouchers(activeV);
    } catch (e) {
      setGroupPrograms([]);
    }
  };

  useEffect(() => { loadGroupPrograms(); }, []);

  // ---- Enrolment flow ----------------------------------------------------
  const openEnrolDialog = (program) => {
    // Registration-only programs skip the payment picker entirely — one
    // click enrols the user. Kept identical to the "reserved slot" flow
    // so the button label switches to "Anmelden" and no dialog appears.
    if (program?.registration_only) {
      handleEnrolShortcut(program);
      return;
    }
    // Sensible payment-method default: credits > voucher > card, so the
    // dialog opens pre-selected to whatever the customer can actually
    // afford right now.
    const price = Number(program.price || 0);
    if (myCredits >= price) setPayMethod('credits');
    else if (myVouchers.some(v => Number(v.remaining_balance || v.amount) >= price)) setPayMethod('voucher');
    else setPayMethod('card');
    const usableVoucher = myVouchers.find(v => Number(v.remaining_balance || v.amount) >= price);
    setChosenVoucherId(usableVoucher?.id || '');
    setEnrolDialog({ program });
  };

  const handleEnrolShortcut = async (program) => {
    setEnrolling(program.id);
    try {
      const { data } = await API.post(`/training-programs/${program.id}/enroll`, { payment_method: 'none' });
      setGroupPrograms((prev) => prev.map((p) => (p.id === program.id ? data : p)));
      toast.success(L('Registered — see you there!', 'Angemeldet — bis dann!'));
      loadGroupPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Action failed', 'Aktion fehlgeschlagen'));
    } finally {
      setEnrolling(null);
    }
  };

  const submitEnrol = async () => {
    if (!enrolDialog?.program) return;
    const program = enrolDialog.program;
    setEnrolling(program.id);
    try {
      const payload = { payment_method: payMethod };
      if (payMethod === 'voucher') {
        if (!chosenVoucherId) { toast.error(L('Please select a voucher', 'Bitte einen Gutschein wählen')); setEnrolling(null); return; }
        payload.voucher_id = chosenVoucherId;
      }
      if (payMethod === 'card') payload.origin_url = window.location.origin;
      const { data } = await API.post(`/training-programs/${program.id}/enroll`, payload);
      if (data.payment_url) {
        // Datatrans redirect
        toast.success(L('Redirecting to payment…', 'Weiterleitung zur Zahlung…'));
        window.location.href = data.payment_url;
        return;
      }
      setGroupPrograms((prev) => prev.map((p) => (p.id === program.id ? data : p)));
      toast.success(L('Enrolled — see you there!', 'Angemeldet — bis dann!'));
      setEnrolDialog(null);
      loadGroupPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Action failed', 'Aktion fehlgeschlagen'));
    } finally {
      setEnrolling(null);
    }
  };

  const unenrol = async (program) => {
    if (!window.confirm(L(
      `Cancel your enrolment in "${program.title}"? Full refund if more than ${program.cancellation_hours || 8}h before the next session; no refund inside that window.`,
      `Anmeldung für "${program.title}" wirklich stornieren? Volle Rückerstattung, wenn mehr als ${program.cancellation_hours || 8} Std vor dem nächsten Termin — danach keine Rückerstattung.`,
    ))) return;
    setEnrolling(program.id);
    try {
      const { data } = await API.post(`/training-programs/${program.id}/unenroll`);
      setGroupPrograms((prev) => prev.map((p) => (p.id === program.id ? data : p)));
      const msg = data._refund_status === 'refunded'
        ? L('Unenrolled — refund issued.', 'Abgemeldet — Rückerstattung veranlasst.')
        : L('Unenrolled — no refund (inside cutoff).', 'Abgemeldet — keine Rückerstattung (innerhalb Frist).');
      toast.success(msg);
      loadGroupPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Action failed', 'Aktion fehlgeschlagen'));
    } finally {
      setEnrolling(null);
    }
  };

  const joinWaitlist = async (program) => {
    setEnrolling(program.id);
    try {
      const { data } = await API.post(`/training-programs/${program.id}/waitlist/join`);
      setGroupPrograms((prev) => prev.map((p) => (p.id === program.id ? data : p)));
      toast.success(L("You're on the waitlist — we'll email you if a spot opens.", 'Du bist auf der Warteliste — wir mailen dich, wenn ein Platz frei wird.'));
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Action failed', 'Aktion fehlgeschlagen'));
    } finally {
      setEnrolling(null);
    }
  };

  const leaveWaitlist = async (program) => {
    setEnrolling(program.id);
    try {
      const { data } = await API.post(`/training-programs/${program.id}/waitlist/leave`);
      setGroupPrograms((prev) => prev.map((p) => (p.id === program.id ? data : p)));
      toast.success(L('Left the waitlist.', 'Von der Warteliste entfernt.'));
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Action failed', 'Aktion fehlgeschlagen'));
    } finally {
      setEnrolling(null);
    }
  };

  useEffect(() => {
    loadAvailability();
  }, [duration, participants]);

  const loadAvailability = async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`/trainers/availability?duration=${duration}&participants=${participants}&days=14`);
      const list = data?.trainers || [];
      setTrainers(list);
      if (!selectedTrainerId && list.length) setSelectedTrainerId(list[0].id);
    } catch (e) {
      toast.error(L('Failed to load trainer availability', 'Trainerverfügbarkeit konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const activeTrainer = useMemo(() => {
    return trainers.find((t) => t.id === selectedTrainerId) || trainers[0] || null;
  }, [trainers, selectedTrainerId]);

  // Group slots by date for the active trainer
  const slotsByDate = useMemo(() => {
    if (!activeTrainer) return {};
    const map = {};
    for (const s of activeTrainer.slots || []) {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    }
    return map;
  }, [activeTrainer]);

  const availableDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  // Auto-select first date once we have data
  useEffect(() => {
    if (!selectedDate && availableDates.length) setSelectedDate(availableDates[0]);
    else if (selectedDate && !availableDates.includes(selectedDate) && availableDates.length) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  const daySlots = (selectedDate && slotsByDate[selectedDate]) || [];

  const formatDate = (isoDate) => {
    const d = new Date(isoDate + 'T00:00:00');
    const weekday = d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { weekday: 'short' });
    const day = d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: 'short' });
    return { weekday, day };
  };

  const confirmBooking = async () => {
    if (!confirming || !activeTrainer) return;
    try {
      setBooking(true);
      const payload = {
        trainer_id: activeTrainer.id,
        date: confirming.date,
        start_time: confirming.start_time,
        duration_minutes: duration,
        participants,
      };
      const { data } = await API.post('/trainer/customer-book-lesson', payload);
      toast.success(L('Training lesson booked!', 'Trainingsstunde gebucht!'));
      setConfirming(null);
      loadAvailability();
    } catch (e) {
      const msg = e?.response?.data?.detail || L('Booking failed', 'Buchung fehlgeschlagen');
      toast.error(msg);
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="trainings-page">
      {/* Page heading — anchors the view even when no programs exist yet */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="trainings-title">
          {L('Trainings & Classes', 'Trainings & Kurse')}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {L(
            'Sign up for weekly group training sessions. Book and pay for each session individually.',
            'Melde dich für wöchentliche Gruppen-Trainings an. Session für Session buchen und einzeln bezahlen.'
          )}
        </p>
      </div>

      {/* Empty state — shown when no group programs exist yet */}
      {!loading && groupPrograms.length === 0 && (
        <div
          className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-8 text-center space-y-3"
          data-testid="no-group-trainings-empty"
        >
          <div className="w-14 h-14 rounded-full bg-white border border-emerald-200 flex items-center justify-center mx-auto shadow-sm">
            <GraduationCap className="w-7 h-7 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">
            {L('No group trainings open right now', 'Zurzeit keine Gruppen-Trainings offen')}
          </h3>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            {L(
              'New trainings will be published soon. Check back here or contact the club.',
              'Neue Trainings werden bald veröffentlicht. Schau später wieder rein oder melde dich beim Club.'
            )}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
            <a
              href="tel:+41442015050"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition shadow-sm"
            >
              044 201 50 50
            </a>
          </div>
        </div>
      )}

      {/* Group programs — recurring classes anyone can sign up for */}
      {groupPrograms.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
          data-testid="group-programs-section"
        >
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Users size={20} className="text-emerald-600" />
              {L('Group Trainings', 'Gruppen-Trainings')}
            </h2>
            <p className="text-slate-500 text-sm mt-0.5">
              {L('Weekly sessions — book and pay one session at a time.', 'Wöchentliche Sessions — jede einzeln buchen und bezahlen.')}
            </p>
          </div>
          <div className="space-y-3">
            {programGroups.map((g) => {
              const isOpen = expandedGroup === g.key;
              return (
                <div key={g.key} className="border-2 border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`group-program-header-${g.title}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedGroup(isOpen ? null : g.key)}
                    className={`w-full flex items-center justify-between gap-2 p-3 sm:p-4 text-left transition ${isOpen ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                    data-testid={`group-program-toggle-${g.title}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h3 className="font-bold text-slate-900 text-sm sm:text-base leading-tight">{g.title}</h3>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                          {g.total_sessions} {L('sessions', 'Termine')}
                        </span>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-500 mt-1 leading-snug">
                        {L(WEEKDAYS[g.weekday]?.en, WEEKDAYS[g.weekday]?.de)}
                        {' · '}{g.start_time} · {g.duration_minutes} min
                        {g.trainer_name && <> · <span className="font-semibold">{g.trainer_name}</span></>}
                      </p>
                      {g.description && (
                        <>
                          <p className={`text-[11px] sm:text-xs text-slate-600 mt-1 whitespace-pre-line break-words ${isOpen ? '' : 'line-clamp-2'}`} data-testid={`group-program-description-${g.title}`}>
                            {g.description}
                          </p>
                          {!isOpen && (g.description.length > 110 || g.description.includes('\n')) && (
                            <span className="text-[11px] sm:text-xs font-semibold text-emerald-600 mt-0.5 inline-block" data-testid={`group-program-read-more-${g.title}`}>
                              {L('Read more', 'Mehr lesen')}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-right shrink-0 pl-1">
                      <div className="font-bold text-emerald-600 text-sm sm:text-base whitespace-nowrap">
                        {g.registration_only
                          ? <span className="text-purple-600">{L('Registration only', 'Nur Anmeldung')}</span>
                          : <>{Number(g.price).toFixed(0)} CHF</>}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 whitespace-nowrap">
                        {isOpen ? L('Hide', 'Ausblenden') : L('Show', 'Zeigen')} {isOpen ? '▲' : '▼'}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {g.sessions.map((p) => {
                        const isEnrolled = (p.participants || []).some((pt) => pt.user_id === currentUserId);
                        const myWl = (p.waitlist || []).find((w) => w.user_id === currentUserId);
                        const isOffered = myWl?.status === 'offered';
                        const isWaiting = myWl?.status === 'waiting';
                        const isFull = p.spots_left <= 0 && !isEnrolled && !isOffered;
                        const dateStr = p.session_date
                          ? new Date(p.session_date + 'T00:00:00').toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })
                          : L(WEEKDAYS[p.weekday]?.en, WEEKDAYS[p.weekday]?.de);
                        return (
                          <div key={p.id}
                               className={`flex items-center justify-between gap-3 p-3 ${isEnrolled ? 'bg-emerald-50/50' : isOffered ? 'bg-amber-50' : ''}`}
                               data-testid={`group-program-${p.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-semibold text-sm ${p.session_date ? 'text-slate-900' : 'text-slate-500'}`}>{dateStr}</span>
                                {isEnrolled && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold uppercase"><CheckCircle2 size={9} className="inline mr-0.5" />{L('Enrolled', 'Angemeldet')}</span>}
                                {isOffered && <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold uppercase"><Hourglass size={9} className="inline mr-0.5" />{L('Offered', 'Angeboten')}</span>}
                                {isWaiting && <span className="text-[10px] bg-slate-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">{L('Waitlist', 'Warteliste')}</span>}
                              </div>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                {p.spots_left > 0
                                  ? L(`${p.spots_left} spot${p.spots_left === 1 ? '' : 's'} left`, `${p.spots_left} Platz${p.spots_left === 1 ? '' : 'e'} frei`)
                                  : L('Full', 'Ausgebucht')}
                                {p.waitlist_count > 0 && <> · <span className="text-amber-600 font-semibold">{p.waitlist_count} {L('waiting', 'Warteliste')}</span></>}
                              </p>
                              {isOffered && myWl?.offer_expires_at && (
                                <p className="text-[10px] text-amber-800 mt-0.5 font-semibold">
                                  {L('Claim by', 'Bestätigen bis')}: {new Date(myWl.offer_expires_at).toLocaleString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                              {/* Public roster: every signed-up player with their
                                  current level. Visible to everyone so people
                                  can judge the skill mix before enrolling.
                                  Mirrors the Mixin colour-scale (6+ pure green,
                                  4-5.9 yellow, 2-3.9 orange, <2 red). */}
                              {(p.participants || []).length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`group-program-roster-${p.id}`}>
                                  {p.participants.map((pt) => {
                                    const lvl = pt.player_level;
                                    const lvlNum = typeof lvl === 'number' ? lvl : null;
                                    let lvlCls = 'bg-slate-100 text-slate-500';
                                    if (lvlNum != null) {
                                      if (lvlNum < 2) lvlCls = 'bg-red-500 text-white';
                                      else if (lvlNum < 4) lvlCls = 'bg-orange-500 text-white';
                                      else if (lvlNum < 6) lvlCls = 'bg-yellow-500 text-slate-900';
                                      else lvlCls = 'bg-green-500 text-white';
                                    }
                                    return (
                                      <span key={pt.user_id}
                                            className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-2 pr-1 py-0.5 text-[11px] font-medium text-slate-700"
                                            data-testid={`group-program-player-${p.id}-${pt.user_id}`}>
                                        <span className="truncate max-w-[120px]">{pt.user_name || '—'}</span>
                                        <span className={`inline-flex items-center justify-center min-w-[32px] h-4 rounded-full font-bold text-[10px] px-1 ${lvlCls}`}>
                                          {lvlNum != null ? lvlNum.toFixed(2) : '?'}
                                        </span>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <div className="shrink-0">
                              {isEnrolled ? (
                                <Button size="sm" onClick={() => unenrol(p)} disabled={enrolling === p.id} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs" data-testid={`group-program-btn-${p.id}`}>
                                  {enrolling === p.id ? '…' : L('Unenroll', 'Abmelden')}
                                </Button>
                              ) : isOffered ? (
                                <div className="flex gap-1">
                                  <Button size="sm" onClick={() => openEnrolDialog(p)} disabled={enrolling === p.id} className="bg-amber-500 hover:bg-amber-600 text-white h-8 text-xs font-bold" data-testid={`group-program-claim-${p.id}`}>
                                    {L('Claim', 'Beanspruchen')}
                                  </Button>
                                  <Button size="sm" onClick={() => leaveWaitlist(p)} disabled={enrolling === p.id} variant="outline" className="h-8 text-xs" data-testid={`group-program-decline-${p.id}`}>
                                    {L('Decline', 'Ablehnen')}
                                  </Button>
                                </div>
                              ) : isWaiting ? (
                                <Button size="sm" onClick={() => leaveWaitlist(p)} disabled={enrolling === p.id} variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" data-testid={`group-program-leave-wl-${p.id}`}>
                                  {enrolling === p.id ? '…' : L('Leave', 'Verlassen')}
                                </Button>
                              ) : isFull ? (
                                <Button size="sm" onClick={() => joinWaitlist(p)} disabled={enrolling === p.id} className="bg-slate-700 hover:bg-slate-800 text-white h-8 text-xs" data-testid={`group-program-join-wl-${p.id}`}>
                                  {enrolling === p.id ? '…' : L('Waitlist', 'Warteliste')}
                                </Button>
                              ) : (
                                <Button size="sm" onClick={() => openEnrolDialog(p)} disabled={enrolling === p.id} className="btn-primary h-8 text-xs" data-testid={`group-program-btn-${p.id}`}>
                                  {enrolling === p.id ? '…' : L('Enroll', 'Anmelden')}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Spontaneous training (individual lessons with a trainer) — temporarily
          hidden per user request 2026-02-25: clients are focusing on the
          weekly group programs above. To re-enable, flip the flag below. */}
      {false && (<>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between gap-4 flex-wrap"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="trainings-title-spontaneous">
            {L('Spontaneous Training', 'Spontanes Training')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {L(
              'Browse trainer availability and reserve your lesson. Bookable 08:00 – 20:00, 24 h in advance. Times outside this window are on request — please contact the club.',
              'Trainerverfügbarkeit durchstöbern und Stunde reservieren. Buchbar 08:00 – 20:00, 24 h im Voraus. Zeiten davor oder danach nur auf Anfrage — bitte Club kontaktieren.'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              {L('Duration', 'Dauer')}
            </label>
            <div className="w-24 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-sm font-semibold text-slate-700">
              60 min
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              {L('Players', 'Spieler')}
            </label>
            <Select value={String(participants)} onValueChange={(v) => setParticipants(Number(v))}>
              <SelectTrigger className="w-24" data-testid="participants-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="4">4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </motion.div>

      {(() => {
        const hasAnySlots = trainers.some((t) => (t.slots || []).length > 0);
        if (!loading && !hasAnySlots) {
          return (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-8 text-center space-y-3"
              data-testid="no-availability-empty"
            >
              <div className="w-14 h-14 rounded-full bg-white border border-emerald-200 flex items-center justify-center mx-auto shadow-sm">
                <GraduationCap className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                {L('No open lessons right now', 'Zurzeit keine offenen Stunden')}
              </h3>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                {L(
                  'Please contact the club to organise a training.',
                  'Bitte kontaktiere den Club, um ein Training zu vereinbaren.'
                )}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
                <a
                  href="tel:+41442015050"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition shadow-sm"
                  data-testid="empty-call-club"
                >
                  044 201 50 50
                </a>
                <a
                  href="mailto:info@padelarena.ch"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-emerald-400 text-slate-700 font-semibold text-sm transition"
                  data-testid="empty-mail-club"
                >
                  info@padelarena.ch
                </a>
              </div>
            </motion.div>
          );
        }
        return null;
      })()}

      {/* Trainer chips */}
      {(loading || trainers.some((t) => (t.slots || []).length > 0)) && (
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="trainer-chips">
        {loading && trainers.length === 0 ? (
          <div className="h-10 w-40 bg-slate-100 rounded-full animate-pulse" />
        ) : (
          trainers.map((t) => {
            const active = t.id === selectedTrainerId;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTrainerId(t.id)}
                data-testid={`trainer-chip-${t.name.split(' ')[0].toLowerCase()}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition font-semibold text-sm border ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                }`}
              >
                <GraduationCap className="w-4 h-4" />
                {t.name}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/25' : 'bg-emerald-50 text-emerald-700'}`}>
                  {t.slots?.length || 0}
                </span>
              </button>
            );
          })
        )}
      </div>
      )}

      {/* Trainer bio card */}
      {activeTrainer && (activeTrainer.bio || activeTrainer.photo_url) && (
        <Card className="p-4 flex items-center gap-4 border-slate-200">
          {activeTrainer.photo_url ? (
            <img src={activeTrainer.photo_url} alt={activeTrainer.name} className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <GraduationCap className="w-7 h-7 text-emerald-700" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-slate-900 font-bold text-lg leading-tight">{activeTrainer.name}</p>
            {activeTrainer.bio && (
              <p className="text-slate-500 text-sm leading-snug truncate">{activeTrainer.bio}</p>
            )}
          </div>
        </Card>
      )}

      {/* Date scroller — hidden when the empty-state card is showing above */}
      {(loading || trainers.some((t) => (t.slots || []).length > 0)) && (
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="date-scroller">
        {availableDates.length === 0 && !loading ? (
          <p className="text-slate-500 text-sm py-4">
            {L('No available slots in the next 14 days.', 'Keine freien Slots in den nächsten 14 Tagen.')}
          </p>
        ) : (
          availableDates.map((dt) => {
            const { weekday, day } = formatDate(dt);
            const active = dt === selectedDate;
            const count = slotsByDate[dt]?.length || 0;
            return (
              <button
                key={dt}
                onClick={() => setSelectedDate(dt)}
                data-testid={`date-btn-${dt}`}
                className={`flex flex-col items-center justify-center min-w-[76px] px-3 py-3 rounded-2xl transition border ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}
              >
                <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-emerald-300' : 'text-emerald-600'}`}>{weekday}</span>
                <span className="text-base font-bold mt-0.5">{day}</span>
                <span className={`text-[10px] font-medium mt-0.5 ${active ? 'text-white/70' : 'text-slate-400'}`}>{count}× {L('slot', 'Slot')}</span>
              </button>
            );
          })
        )}
      </div>
      )}

      {/* Slots grid — hidden when the empty-state card is showing above */}
      {(loading || trainers.some((t) => (t.slots || []).length > 0)) && (
      <>{loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[1,2,3,4,5,6,7,8].map((i) => (
            <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="slots-grid">
          {daySlots.length === 0 && selectedDate && (
            <p className="col-span-full text-slate-500 text-sm py-8 text-center">
              {L('No open slots for this day.', 'Keine offenen Slots an diesem Tag.')}
            </p>
          )}
          {daySlots.map((s, i) => (
            <motion.button
              key={`${s.date}-${s.start_time}-${i}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.015 }}
              onClick={() => setConfirming(s)}
              data-testid={`slot-${s.date}-${s.start_time}`}
              className="group text-left p-4 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 hover:shadow-md transition"
            >
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-slate-900 text-lg leading-none">{s.start_time}</span>
                <span className="text-slate-400 text-[11px] group-hover:text-emerald-600 whitespace-nowrap">→ {s.end_time}</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-2">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">{s.court_name}</span>
              </div>
              <div className="flex items-center justify-between gap-1 mt-2">
                <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50 px-1.5 py-0 h-5">
                  {s.tier === 'peak' ? L('Peak', 'Peak') : L('Off-peak', 'Off-peak')}
                </Badge>
                <span className="font-bold text-emerald-700 text-sm whitespace-nowrap">{s.price_per_person} CHF</span>
              </div>
            </motion.button>
          ))}
        </div>
      )}</>
      )}
      </>)}
      {/* end spontaneous-training feature flag */}

      {/* Confirm dialog */}
      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              {L('Confirm training lesson', 'Trainingsstunde bestätigen')}
            </DialogTitle>
            <DialogDescription>
              {L(
                'This will book the lesson with the trainer. Payment is settled at the club unless you pay online in your bookings later.',
                'Damit buchst du die Stunde beim Trainer. Zahlung erfolgt vor Ort oder später online über deine Buchungen.'
              )}
            </DialogDescription>
          </DialogHeader>
          {confirming && activeTrainer && (
            <div className="space-y-3 py-2 text-sm">
              <Row icon={<GraduationCap className="w-4 h-4" />} label={L('Trainer', 'Trainer')} value={activeTrainer.name} />
              <Row icon={<Calendar className="w-4 h-4" />} label={L('Date', 'Datum')} value={formatDate(confirming.date).weekday + ', ' + formatDate(confirming.date).day} />
              <Row icon={<Clock className="w-4 h-4" />} label={L('Time', 'Zeit')} value={`${confirming.start_time} — ${confirming.end_time} (${duration} min)`} />
              <Row icon={<MapPin className="w-4 h-4" />} label={L('Court', 'Court')} value={confirming.court_name} />
              <Row icon={<Users className="w-4 h-4" />} label={L('Players', 'Spieler')} value={String(participants)} />
              <div className="pt-3 border-t flex items-center justify-between">
                <span className="text-slate-500 text-xs uppercase tracking-wider font-bold">{L('Total', 'Total')}</span>
                <span className="font-black text-emerald-700 text-2xl">{confirming.price_total} CHF</span>
              </div>
              <p className="text-[11px] text-slate-500">{L(`${confirming.price_per_person} CHF per player · ${confirming.tier || ''}`, `${confirming.price_per_person} CHF pro Person · ${confirming.tier || ''}`)}</p>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setConfirming(null)}
              disabled={booking}
              data-testid="cancel-booking"
            >
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={confirmBooking}
              disabled={booking}
              data-testid="confirm-booking"
            >
              {booking ? L('Booking…', 'Buche…') : (
                <>
                  {L('Book Lesson', 'Stunde buchen')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group-program enrol / claim dialog with payment picker */}
      <Dialog open={!!enrolDialog} onOpenChange={(o) => !o && setEnrolDialog(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="group-enrol-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-emerald-600" />
              {L('Enrol in group training', 'Für Gruppen-Training anmelden')}
            </DialogTitle>
            <DialogDescription>
              {L(
                'Choose how you want to pay. One signup = every recurring session covered.',
                'Wähle die Zahlungsart. Einmal anmelden = alle wöchentlichen Sessions inklusive.',
              )}
            </DialogDescription>
          </DialogHeader>
          {enrolDialog?.program && (
            <div className="space-y-3 py-2 text-sm">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900">{enrolDialog.program.title}</div>
                    <div className="text-xs text-slate-600">
                      {L(WEEKDAYS[enrolDialog.program.weekday]?.en, WEEKDAYS[enrolDialog.program.weekday]?.de)} · {enrolDialog.program.start_time}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{L('Total', 'Total')}</div>
                    <div className="text-2xl font-black text-emerald-700">{Number(enrolDialog.program.price).toFixed(0)} CHF</div>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-slate-500">
                {L(
                  `Cancellation policy: free refund until ${enrolDialog.program.cancellation_hours || 8}h before the next session — no refund after that.`,
                  `Stornobedingungen: Rückerstattung bis ${enrolDialog.program.cancellation_hours || 8} Std vor der nächsten Session — danach keine Rückerstattung.`,
                )}
              </p>

              <div className="space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {L('Payment method', 'Zahlungsart')}
                </div>

                {/* Credits */}
                <button
                  type="button"
                  disabled={myCredits < Number(enrolDialog.program.price)}
                  onClick={() => setPayMethod('credits')}
                  data-testid="enrol-pay-credits"
                  className={`w-full flex items-center gap-3 rounded-lg border-2 p-3 transition text-left ${
                    payMethod === 'credits' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                  } ${myCredits < Number(enrolDialog.program.price) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Wallet size={20} className={payMethod === 'credits' ? 'text-emerald-600' : 'text-slate-400'} />
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{L('Credits balance', 'Guthaben')}</div>
                    <div className="text-xs text-slate-500">{L('Available', 'Verfügbar')}: {myCredits.toFixed(2)} CHF</div>
                  </div>
                  {payMethod === 'credits' && <CheckCircle2 size={18} className="text-emerald-600" />}
                </button>

                {/* Voucher — auto-deduct now */}
                {myVouchers.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPayMethod('voucher')}
                    data-testid="enrol-pay-voucher"
                    className={`w-full flex items-center gap-3 rounded-lg border-2 p-3 transition text-left ${
                      payMethod === 'voucher' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <TicketPercent size={20} className={payMethod === 'voucher' ? 'text-emerald-600' : 'text-slate-400'} />
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 text-sm">{L('Voucher / Abo — redeem now', 'Gutschein / Abo — jetzt einlösen')}</div>
                      {payMethod === 'voucher' ? (
                        <Select value={chosenVoucherId} onValueChange={setChosenVoucherId}>
                          <SelectTrigger className="mt-1 h-8 text-xs" data-testid="enrol-voucher-select" onClick={(e) => e.stopPropagation()}>
                            <SelectValue placeholder={L('Select voucher…', 'Gutschein wählen…')} />
                          </SelectTrigger>
                          <SelectContent>
                            {myVouchers.map((v) => (
                              <SelectItem key={v.id} value={v.id} disabled={Number(v.remaining_balance || v.amount) < Number(enrolDialog.program.price)}>
                                {v.code} — {Number(v.remaining_balance || v.amount).toFixed(2)} CHF
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-xs text-slate-500">{myVouchers.length} {L('available', 'verfügbar')}</div>
                      )}
                    </div>
                    {payMethod === 'voucher' && <CheckCircle2 size={18} className="text-emerald-600" />}
                  </button>
                )}

                {/* Voucher — redeem at reception (manual). Only when the user
                    actually owns an active voucher that covers the price. */}
                {myVouchers.some(v => Number(v.remaining_balance || v.amount) >= Number(enrolDialog.program.price)) && (
                  <button
                    type="button"
                    onClick={() => setPayMethod('voucher_at_desk')}
                    data-testid="enrol-pay-voucher-at-desk"
                    className={`w-full flex items-center gap-3 rounded-lg border-2 p-3 transition text-left ${
                      payMethod === 'voucher_at_desk' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <TicketPercent size={20} className={payMethod === 'voucher_at_desk' ? 'text-emerald-600' : 'text-slate-400'} />
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 text-sm">{L('I have a voucher — redeem at reception', 'Ich habe einen Gutschein — Empfang löst ein')}</div>
                      <div className="text-xs text-slate-500">{L('Your spot is reserved immediately. Reception will redeem the voucher when you arrive.', 'Dein Platz ist sofort reserviert. Empfang löst den Gutschein bei deinem Besuch ein.')}</div>
                    </div>
                    {payMethod === 'voucher_at_desk' && <CheckCircle2 size={18} className="text-emerald-600" />}
                  </button>
                )}

                {/* Card */}
                <button
                  type="button"
                  onClick={() => setPayMethod('card')}
                  data-testid="enrol-pay-card"
                  className={`w-full flex items-center gap-3 rounded-lg border-2 p-3 transition text-left ${
                    payMethod === 'card' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <CreditCard size={20} className={payMethod === 'card' ? 'text-emerald-600' : 'text-slate-400'} />
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{L('Card / TWINT / Apple Pay', 'Karte / TWINT / Apple Pay')}</div>
                    <div className="text-xs text-slate-500">{L('Secure payment via Datatrans', 'Sichere Zahlung via Datatrans')}</div>
                  </div>
                  {payMethod === 'card' && <CheckCircle2 size={18} className="text-emerald-600" />}
                </button>
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setEnrolDialog(null)}
              disabled={!!enrolling}
              data-testid="enrol-cancel"
            >
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={submitEnrol}
              disabled={!!enrolling}
              data-testid="enrol-confirm"
            >
              {enrolling ? L('Processing…', 'Verarbeiten…') : L('Confirm & Pay', 'Bestätigen & Bezahlen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 text-slate-700">
    <span className="text-slate-400">{icon}</span>
    <span className="text-slate-500 text-xs uppercase tracking-wider font-bold w-16">{label}</span>
    <span className="font-semibold flex-1 text-right truncate">{value}</span>
  </div>
);export default Trainings;
